import type {
  ProviderApprovalDecision,
  ProviderApprovalOption,
  ProviderRequestKind,
  ProviderUserInputAnswers,
  TurnId,
} from "@afar-research/contracts";
import { ApprovalRequestId, type OrchestrationThreadActivity } from "@afar-research/contracts";

type AttentionStatus = "unresolved" | "resolved" | "expired";

interface ThreadAttentionRequestBase {
  readonly requestId: ApprovalRequestId;
  readonly turnId: TurnId | null;
  readonly status: AttentionStatus;
  readonly callbackViable: boolean;
  readonly requestedAt: string;
  readonly resolvedAt: string | null;
}

export interface ThreadApprovalAttentionRequest extends ThreadAttentionRequestBase {
  readonly type: "approval";
  readonly requestKind: ProviderRequestKind | null;
  /** The connector or app an MCP elicitation names, when it names one. */
  readonly appName: string | null;
  /** Provider-advertised choices; empty means the client's standard pair. */
  readonly options: ReadonlyArray<ProviderApprovalOption>;
  readonly resolution: ProviderApprovalDecision | null;
}

export interface ThreadUserInputQuestion {
  readonly questionId: string;
  readonly prompt: string;
  readonly options: ReadonlyArray<string>;
  readonly allowsMultiple: boolean;
}

export interface ThreadUserInputAttentionRequest extends ThreadAttentionRequestBase {
  readonly type: "user-input";
  readonly questions: ReadonlyArray<ThreadUserInputQuestion>;
  readonly resolution: { readonly answeredQuestionIds: ReadonlyArray<string> } | null;
}

export interface ThreadUserInputAnswer {
  readonly questionId: string;
  readonly values: ReadonlyArray<string>;
}

const recordPayload = (value: unknown): Readonly<Record<string, unknown>> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;

const requestIdFrom = (activity: OrchestrationThreadActivity): ApprovalRequestId | null => {
  const requestId = recordPayload(activity.payload)?.requestId;
  return typeof requestId === "string" && requestId.trim().length > 0
    ? ApprovalRequestId.make(requestId)
    : null;
};

const isApprovalDecision = (value: unknown): value is ProviderApprovalDecision =>
  value === "accept" ||
  value === "acceptForSession" ||
  value === "acceptAlways" ||
  value === "decline" ||
  value === "cancel";

const isRequestKind = (value: unknown): value is ProviderRequestKind =>
  value === "command" ||
  value === "file-read" ||
  value === "file-change" ||
  value === "mcp-elicitation";

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

/**
 * Providers advertise their own approval choices for requests where the
 * standard pair is wrong, such as an MCP elicitation naming the connector it
 * wants to reach. An option the client cannot act on is worse than none, so a
 * malformed entry is dropped rather than rendered.
 */
const normalizeApprovalOptions = (value: unknown): ReadonlyArray<ProviderApprovalOption> => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((candidate) => {
    const option = recordPayload(candidate);
    const label = nonEmptyString(option?.label);
    if (option === null || label === null || !isApprovalDecision(option.decision)) return [];
    if (seen.has(option.decision)) return [];
    seen.add(option.decision);
    const warning = nonEmptyString(option.warning);
    return [{ decision: option.decision, label, ...(warning ? { warning } : {}) }];
  });
};

export type ThreadAttentionActivityTransition = {
  readonly type: "approval" | "user-input";
  readonly requestId: ApprovalRequestId;
  readonly outcome: "requested" | "resolved" | "expired";
  readonly source: "requested" | "response-authorized" | "provider-resolved" | "expired";
};

export const classifyThreadAttentionActivity = (
  activity: OrchestrationThreadActivity,
): ThreadAttentionActivityTransition | null => {
  const requestId = requestIdFrom(activity);
  if (requestId === null) return null;
  switch (activity.kind) {
    case "approval.requested":
      return { type: "approval", requestId, outcome: "requested", source: "requested" };
    case "approval.response-authorized":
      return {
        type: "approval",
        requestId,
        outcome: "resolved",
        source: "response-authorized",
      };
    case "approval.resolved":
      return {
        type: "approval",
        requestId,
        outcome: "resolved",
        source: "provider-resolved",
      };
    case "approval.expired":
      return { type: "approval", requestId, outcome: "expired", source: "expired" };
    case "user-input.requested":
      return { type: "user-input", requestId, outcome: "requested", source: "requested" };
    case "user-input.response-authorized":
      return {
        type: "user-input",
        requestId,
        outcome: "resolved",
        source: "response-authorized",
      };
    case "user-input.resolved":
      return {
        type: "user-input",
        requestId,
        outcome: "resolved",
        source: "provider-resolved",
      };
    case "user-input.expired":
      return { type: "user-input", requestId, outcome: "expired", source: "expired" };
    default:
      return null;
  }
};

const normalizeQuestions = (value: unknown): ReadonlyArray<ThreadUserInputQuestion> => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((candidate) => {
    const question = recordPayload(candidate);
    const id = question?.id;
    const prompt = question?.question;
    if (
      question === null ||
      typeof id !== "string" ||
      id.trim().length === 0 ||
      seen.has(id) ||
      typeof prompt !== "string" ||
      prompt.trim().length === 0
    ) {
      return [];
    }
    seen.add(id);
    const options = Array.isArray(question.options)
      ? question.options.flatMap((candidateOption) => {
          const label = recordPayload(candidateOption)?.label;
          return typeof label === "string" && label.trim().length > 0 ? [label] : [];
        })
      : [];
    return [
      {
        questionId: id,
        prompt,
        options,
        allowsMultiple: question.multiSelect === true,
      },
    ];
  });
};

export const deriveThreadAttentionRequests = (
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): {
  readonly approvals: ReadonlyArray<ThreadApprovalAttentionRequest>;
  readonly userInputs: ReadonlyArray<ThreadUserInputAttentionRequest>;
} => {
  const approvals = new Map<ApprovalRequestId, ThreadApprovalAttentionRequest>();
  const userInputs = new Map<ApprovalRequestId, ThreadUserInputAttentionRequest>();

  for (const activity of activities) {
    const transition = classifyThreadAttentionActivity(activity);
    if (transition === null) continue;
    const { requestId } = transition;
    const payload = recordPayload(activity.payload);
    if (transition.type === "approval") {
      switch (transition.outcome) {
        case "requested": {
          if (approvals.has(requestId)) break;
          approvals.set(requestId, {
            type: "approval",
            requestId,
            turnId: activity.turnId,
            requestKind: isRequestKind(payload?.requestKind) ? payload.requestKind : null,
            appName: nonEmptyString(payload?.appName),
            options: normalizeApprovalOptions(payload?.options),
            status: "unresolved",
            callbackViable: true,
            resolution: null,
            requestedAt: activity.createdAt,
            resolvedAt: null,
          });
          break;
        }
        case "resolved": {
          const existing = approvals.get(requestId);
          if (existing === undefined || existing.status !== "unresolved") break;
          approvals.set(requestId, {
            ...existing,
            status: "resolved",
            callbackViable: false,
            resolution: isApprovalDecision(payload?.decision) ? payload.decision : null,
            resolvedAt: activity.createdAt,
          });
          break;
        }
        case "expired": {
          const existing = approvals.get(requestId);
          if (existing === undefined) {
            approvals.set(requestId, {
              type: "approval",
              requestId,
              turnId: activity.turnId,
              requestKind: null,
              appName: null,
              options: [],
              status: "expired",
              callbackViable: false,
              resolution: null,
              requestedAt: activity.createdAt,
              resolvedAt: activity.createdAt,
            });
            break;
          }
          if (existing.status !== "unresolved") break;
          approvals.set(requestId, {
            ...existing,
            status: "expired",
            callbackViable: false,
            resolvedAt: activity.createdAt,
          });
          break;
        }
      }
      continue;
    }
    switch (transition.outcome) {
      case "requested": {
        if (userInputs.has(requestId)) break;
        userInputs.set(requestId, {
          type: "user-input",
          requestId,
          turnId: activity.turnId,
          questions: normalizeQuestions(payload?.questions),
          status: "unresolved",
          callbackViable: true,
          resolution: null,
          requestedAt: activity.createdAt,
          resolvedAt: null,
        });
        break;
      }
      case "resolved": {
        const existing = userInputs.get(requestId);
        if (existing === undefined || existing.status !== "unresolved") break;
        const answers = recordPayload(payload?.answers);
        const answeredQuestionIds =
          transition.source === "response-authorized" && Array.isArray(payload?.questionIds)
            ? payload.questionIds.filter(
                (questionId): questionId is string => typeof questionId === "string",
              )
            : answers === null
              ? existing.questions.map((question) => question.questionId)
              : Object.keys(answers);
        userInputs.set(requestId, {
          ...existing,
          status: "resolved",
          callbackViable: false,
          resolution: { answeredQuestionIds },
          resolvedAt: activity.createdAt,
        });
        break;
      }
      case "expired": {
        const existing = userInputs.get(requestId);
        if (existing === undefined) {
          userInputs.set(requestId, {
            type: "user-input",
            requestId,
            turnId: activity.turnId,
            questions: [],
            status: "expired",
            callbackViable: false,
            resolution: null,
            requestedAt: activity.createdAt,
            resolvedAt: activity.createdAt,
          });
          break;
        }
        if (existing.status !== "unresolved") break;
        userInputs.set(requestId, {
          ...existing,
          status: "expired",
          callbackViable: false,
          resolvedAt: activity.createdAt,
        });
        break;
      }
    }
  }

  return { approvals: [...approvals.values()], userInputs: [...userInputs.values()] };
};

export type ThreadUserInputAnswerValidation =
  | {
      readonly outcome: "valid";
      readonly answers: ProviderUserInputAnswers;
      readonly answeredQuestionIds: ReadonlyArray<string>;
    }
  | {
      readonly outcome: "invalid";
      readonly reason:
        | "missing-answer"
        | "duplicate-answer"
        | "unknown-question"
        | "malformed-answer";
      readonly questionId: string;
    };

export const validateThreadUserInputAnswers = (
  request: ThreadUserInputAttentionRequest,
  supplied: ReadonlyArray<ThreadUserInputAnswer>,
): ThreadUserInputAnswerValidation => {
  const suppliedByQuestion = new Map<string, ThreadUserInputAnswer>();
  const known = new Set(request.questions.map((question) => question.questionId));
  for (const answer of supplied) {
    if (!known.has(answer.questionId)) {
      return { outcome: "invalid", reason: "unknown-question", questionId: answer.questionId };
    }
    if (suppliedByQuestion.has(answer.questionId)) {
      return { outcome: "invalid", reason: "duplicate-answer", questionId: answer.questionId };
    }
    suppliedByQuestion.set(answer.questionId, answer);
  }

  const answers: Record<string, string | ReadonlyArray<string>> = {};
  for (const question of request.questions) {
    const answer = suppliedByQuestion.get(question.questionId);
    if (answer === undefined) {
      return {
        outcome: "invalid",
        reason: "missing-answer",
        questionId: question.questionId,
      };
    }
    if (
      answer.values.length === 0 ||
      answer.values.some((value) => value.trim().length === 0) ||
      (!question.allowsMultiple && answer.values.length !== 1)
    ) {
      return {
        outcome: "invalid",
        reason: "malformed-answer",
        questionId: question.questionId,
      };
    }
    answers[question.questionId] = question.allowsMultiple ? answer.values : answer.values[0]!;
  }
  return {
    outcome: "valid",
    answers,
    answeredQuestionIds: request.questions.map((question) => question.questionId),
  };
};

export const validateProviderThreadUserInputAnswers = (
  request: ThreadUserInputAttentionRequest,
  answers: ProviderUserInputAnswers,
): ThreadUserInputAnswerValidation => {
  const supplied: ThreadUserInputAnswer[] = [];
  for (const [questionId, value] of Object.entries(answers)) {
    if (typeof value === "string") {
      supplied.push({ questionId, values: [value] });
      continue;
    }
    if (Array.isArray(value)) {
      if (!value.every((entry) => typeof entry === "string")) {
        return { outcome: "invalid", reason: "malformed-answer", questionId };
      }
      supplied.push({ questionId, values: value });
      continue;
    }
    const encoded = recordPayload(value);
    if (
      encoded !== null &&
      Array.isArray(encoded.answers) &&
      encoded.answers.every((entry) => typeof entry === "string")
    ) {
      supplied.push({ questionId, values: encoded.answers });
      continue;
    }
    return { outcome: "invalid", reason: "malformed-answer", questionId };
  }
  return validateThreadUserInputAnswers(request, supplied);
};
